/**
 * Accessible drag-and-drop file picker (P2-7, UX-REVIEW §4 item 7).
 *
 * Replaces the ad-hoc hidden ghost `<input type=file>` + divs scattered across the upload
 * surfaces (Generate, Create-a-Deliverable, Setup/migrate, Batch, Profile) with ONE
 * consistent, keyboard-operable control:
 *
 *  - the drop target is a real `<button>` (keyboard-activatable, focus-visible ring),
 *  - `onDragOver`/`onDragLeave`/`onDrop` drive a visible drag-active state,
 *  - `accept` limits the native picker,
 *  - clear label + sublabel + chosen-file display,
 *  - `loading` (uploading) and `disabled` states.
 *
 * The hidden `<input>` only exists to open the OS file dialog; it is `aria-hidden` and
 * `tabIndex=-1` so it never becomes a second tab stop — the button is the single control.
 */
import { useId, useRef, useState } from "react";
import { Spinner } from "./Spinner";

export interface DropzoneProps {
  /** Bold primary line, e.g. "Drag your Element export here". */
  label: string;
  /** Quieter helper line under the label (browse hint, accepted types…). */
  sublabel?: string;
  /** Extra faint line (e.g. ".zip also works"). */
  hint?: string;
  /** Native file-dialog accept string, e.g. ".mdb,application/x-msaccess". */
  accept?: string;
  /** Name of the currently chosen/uploaded file, shown as a chip when present. */
  selectedName?: string | null;
  /** Shown while an upload is in flight. */
  loading?: boolean;
  loadingLabel?: string;
  disabled?: boolean;
  /** Accessible label for the control (defaults to `label`). */
  ariaLabel?: string;
  /** Called with the chosen File (first file only). */
  onFile: (file: File) => void;
}

export function Dropzone({
  label,
  sublabel,
  hint,
  accept,
  selectedName,
  loading = false,
  loadingLabel,
  disabled = false,
  ariaLabel,
  onFile,
}: DropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const descId = useId();
  const inactive = disabled || loading;

  const openPicker = () => {
    if (inactive) return;
    inputRef.current?.click();
  };

  const handleFiles = (files: FileList | null | undefined) => {
    const file = files?.[0];
    if (file) onFile(file);
  };

  return (
    <div className="dropzone-wrap">
      <button
        type="button"
        className={`dropzone ${dragging ? "dropzone--active" : ""}`.trim()}
        aria-label={ariaLabel ?? label}
        aria-describedby={sublabel || hint || selectedName ? descId : undefined}
        aria-busy={loading || undefined}
        disabled={inactive}
        onClick={openPicker}
        onDragOver={(e) => {
          if (inactive) return;
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (inactive) return;
          handleFiles(e.dataTransfer.files);
        }}
      >
        {loading ? (
          <span className="row" style={{ justifyContent: "center", gap: "var(--space-2)" }}>
            <Spinner /> <span>{loadingLabel ?? label}</span>
          </span>
        ) : (
          <span className="dropzone__body">
            <span className="dropzone__icon" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 16V4" />
                <path d="m7 9 5-5 5 5" />
                <path d="M5 16v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2" />
              </svg>
            </span>
            <strong className="dropzone__label">{label}</strong>
            {sublabel && <span className="faint" id={descId}>{sublabel}</span>}
            {hint && <span className="faint">{hint}</span>}
          </span>
        )}
      </button>

      {selectedName && !loading && (
        <p className="dropzone__chosen faint" id={descId} style={{ margin: 0 }}>
          {selectedName}
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.currentTarget.value = "";
        }}
      />
    </div>
  );
}
